/**
 * Roughly where a time zone is, and how roughly.
 *
 * A zone is not a place, and this does not pretend otherwise: it answers "near
 * enough to compute a sunset" for somebody who has not told us where they are.
 * It exists because the alternative is worse — the only thing observed about a
 * person is the zone their browser reported, and a zone alone cannot give a
 * shkia, so without this a rest rule could not be applied to them at all.
 *
 * **Every answer from here is approximate, and each says by how much.** A zone
 * can span two thousand kilometers, and sunset moves with latitude and
 * longitude far more than a clock does: the same zone covers Detroit and Miami.
 * So a place carries its zone's measured `spreadMinutes`, and the caller widens
 * the rest window by it rather than trusting a boundary computed from one city.
 *
 * The way out of the approximation is to ASK, which the settings screen does.
 * A stated address is exact and never consults this table.
 */

export interface ZonePlace {
  latitude: number
  longitude: number
  /**
   * How far out a sunset computed at these coordinates can be, in minutes, for
   * somebody else in the same zone.
   *
   * Measured rather than guessed: the sunset here against the sunset at the
   * zone's check cities, on every date of four years, taking the worst of them
   * and rounding up to the next five.
   *
   * **The check cities include the zone's extreme of habitation, however small
   * it is**, because that is the question this field asks. It is not "where is
   * an unknown person likely to be" — that is what the representative above
   * answers — it is "how wrong can this be for anybody in the zone", and a rule
   * that measured only the big cities made the sentence at the top of this
   * comment false. Utqiagvik is 235 minutes from Anchorage where Fairbanks is
   * 95, and a spread of 95 says in writing that nobody in Alaska is further out
   * than that.
   *
   * **The cost is real and it is the cheap one.** Every Alaskan known only by
   * their zone now waits about four hours longer for a digest, so that the four
   * thousand people at Utqiagvik are not mailed during their Shabbos; the
   * Moscow zone widens to five and a quarter hours for Vorkuta. Holding too
   * long is a late digest, and being short is a message in front of somebody on
   * Shabbos — and the way out for anyone the wide window inconveniences is to
   * state an address, which is exact and never consults this table.
   *
   * The measurement is re-run by `zone-spread.test.ts` against the engine that
   * computes the times, city by city, and the stated number must EQUAL what it
   * finds. A spread is the one field here nobody can check by reading it, so a
   * number that is not reproduced is a number that is not checked.
   *
   * It is a sunset spread and nothing more, because the day here ends at
   * `tzeis72fix` — a FIXED seventy-two minutes. Twilight length varies with
   * latitude, but a fixed offset cannot compound it, so the shift in sunset is
   * the whole of the error.
   */
  spreadMinutes: number
}

/**
 * The widest spread a row may state, refused at load rather than reviewed.
 *
 * A wide zone is held with its true spread rather than dropped, so this is not
 * where "we cannot place you" comes from — it is the bound the guard's own
 * arithmetic needs. The rest question is answered by probing a widened window
 * either side of the instant asked about, so the probe window is TWICE the
 * widening, and what it must stay inside is the shortest gap between one rest
 * period and the next — about 22.25 hours, measured.
 *
 * **What the bound protects is liveness, not correctness.** A probe window
 * wider than the gap would find rest at every instant in it and the app would
 * never send at all; short of that, the only cost of a wide window is a late
 * digest. So the ceiling is set where a working day survives in the shortest
 * gap: six hours of spread widens to six and a half, a probe window of thirteen
 * hours, leaving nine and a quarter in which mail goes out.
 *
 * The widest inhabited zone measured is Europe/Moscow at 315 minutes — Moscow
 * against Vorkuta — so a row above this is a mistyped measurement rather than a
 * place, and it stops here rather than in somebody's inbox.
 */
export const PLACE_SPREAD_CEILING_MINUTES = 360

/**
 * The floor on any spread, including a zone one city wide.
 *
 * A representative is a city, not a person, and nobody in it is at the point
 * these coordinates name. Nothing derived from this table is exact. Applied by
 * `placeForZone`, so a row cannot state less by being typed carelessly.
 */
export const PLACE_SPREAD_FLOOR_MINUTES = 5

/**
 * A representative city per zone — the largest population center in it, since
 * that is where an unknown person in that zone most likely is — and the spread
 * measured from it.
 *
 * Not exhaustive, and deliberately so: a table of every IANA zone would be
 * mostly rows nobody resolves, each still an approximation. An unlisted zone is
 * answered by `null`, which the caller must handle as "unknown" rather than as
 * a default location — inventing one would hold somebody's mail against a
 * Shabbos computed for a place they have never been.
 *
 * **A wide zone is held, at its true spread.** China, the Moscow zone, India,
 * Kazakhstan and Finland each span more than the boundary they are trying to
 * locate, which makes their window a poor answer — but the alternative is not a
 * better answer, it is `null`, and null reaches a caller that sends. A window
 * hours too wide costs somebody a late digest; no window at all puts a message
 * in front of them on Shabbos. So the honest answer for a zone we can barely
 * place is to hold generously and ask for an address, never to send.
 *
 * A place that keeps another country's clock a long way away is a row of its
 * own rather than an alias, since an alias hands it the other place's
 * coordinates: Svalbard is on Oslo's clock and two thousand kilometers north of
 * it.
 *
 * **Keys are the names a browser reports, which is not the same as tzdb's
 * canonical set — and several here are tzdb Links onto another zone.**
 * `Europe/Oslo`, `Europe/Stockholm`, `Europe/Copenhagen`, `Arctic/Longyearbyen`
 * and `Atlantic/Jan_Mayen` all link onto `Europe/Berlin`; `Europe/Amsterdam`
 * and `Europe/Luxembourg` onto `Europe/Brussels`, `Europe/Monaco` onto
 * `Europe/Paris`, `America/Curacao` onto `America/Puerto_Rico`.
 *
 * **Folding any of them into its target would be a bug, and a quiet one.** A
 * Link says two places have kept the same CLOCK since 1970; this table is about
 * where the SUN is, and Tromsø reading Berlin's sunset is nineteen degrees of
 * latitude of error. The same fact from the other side is why
 * `America/Coral_Harbour` links onto `America/Panama` and is not an alias here:
 * one is in Nunavut and the other eight degrees north of the equator. So a Link
 * earns an alias only where the aliased place is near enough that the
 * representative's sunset is inside the zone's spread; otherwise it takes a row
 * of its own, as Svalbard does, or it stays unheld.
 *
 * A legacy spelling reaches a row through `canonicalZone`, so nothing here is
 * repeated under a second name.
 */
const ZONE_PLACES: Record<string, ZonePlace> = {
  // The one that is not an approximation of anything else.
  'Asia/Jerusalem': { latitude: 31.78, longitude: 35.22, spreadMinutes: 10 },

  // North America
  'America/New_York': {
    latitude: 40.71,
    longitude: -74.01,
    spreadMinutes: 65,
  },
  'America/Detroit': { latitude: 42.33, longitude: -83.05, spreadMinutes: 35 },
  'America/Toronto': { latitude: 43.65, longitude: -79.38, spreadMinutes: 60 },
  'America/Halifax': { latitude: 44.65, longitude: -63.57, spreadMinutes: 20 },
  'America/Chicago': { latitude: 41.88, longitude: -87.63, spreadMinutes: 85 },
  'America/Winnipeg': { latitude: 49.9, longitude: -97.14, spreadMinutes: 15 },
  'America/Denver': { latitude: 39.74, longitude: -104.99, spreadMinutes: 55 },
  'America/Edmonton': {
    latitude: 53.55,
    longitude: -113.49,
    spreadMinutes: 95,
  },
  'America/Phoenix': { latitude: 33.45, longitude: -112.07, spreadMinutes: 15 },
  'America/Boise': { latitude: 43.61, longitude: -116.2, spreadMinutes: 20 },
  'America/Los_Angeles': {
    latitude: 34.05,
    longitude: -118.24,
    spreadMinutes: 70,
  },
  'America/Vancouver': {
    latitude: 49.28,
    longitude: -123.12,
    spreadMinutes: 30,
  },
  'America/Anchorage': {
    latitude: 61.22,
    longitude: -149.9,
    spreadMinutes: 235,
  },
  'Pacific/Honolulu': {
    latitude: 21.31,
    longitude: -157.86,
    spreadMinutes: 15,
  },
  'America/Indiana/Indianapolis': {
    latitude: 39.77,
    longitude: -86.16,
    spreadMinutes: 10,
  },
  'America/Kentucky/Louisville': {
    latitude: 38.25,
    longitude: -85.76,
    spreadMinutes: 5,
  },
  'America/Mexico_City': {
    latitude: 19.43,
    longitude: -99.13,
    spreadMinutes: 40,
  },
  'America/Tijuana': { latitude: 32.53, longitude: -117.02, spreadMinutes: 10 },
  'America/Panama': { latitude: 8.98, longitude: -79.52, spreadMinutes: 15 },
  'America/Curacao': { latitude: 12.11, longitude: -68.93, spreadMinutes: 10 },

  // South America
  'America/Argentina/Buenos_Aires': {
    latitude: -34.6,
    longitude: -58.38,
    spreadMinutes: 30,
  },
  'America/Sao_Paulo': {
    latitude: -23.55,
    longitude: -46.63,
    spreadMinutes: 45,
  },
  'America/Montevideo': {
    latitude: -34.9,
    longitude: -56.16,
    spreadMinutes: 20,
  },
  'America/Santiago': {
    latitude: -33.45,
    longitude: -70.67,
    spreadMinutes: 40,
  },
  /* Chile is three zones on the mainland, and the two southern regions are
     rows of their own because they are places rather than clocks: Aysén left
     Santiago's daylight-saving rule in 2025 and keeps −3 all year, so reading
     it off Santiago releases Shabbos an hour early through the whole southern
     winter. */
  'America/Coyhaique': {
    latitude: -45.57,
    longitude: -72.07,
    spreadMinutes: 5,
  },
  'America/Punta_Arenas': {
    latitude: -53.16,
    longitude: -70.91,
    spreadMinutes: 15,
  },
  'America/Lima': { latitude: -12.05, longitude: -77.04, spreadMinutes: 40 },
  'America/Bogota': { latitude: 4.71, longitude: -74.07, spreadMinutes: 45 },
  'America/Caracas': { latitude: 10.49, longitude: -66.88, spreadMinutes: 30 },

  // Europe
  'Europe/London': { latitude: 51.51, longitude: -0.13, spreadMinutes: 75 },
  'Europe/Dublin': { latitude: 53.35, longitude: -6.26, spreadMinutes: 20 },
  'Europe/Lisbon': { latitude: 38.72, longitude: -9.14, spreadMinutes: 15 },
  'Europe/Madrid': { latitude: 40.42, longitude: -3.7, spreadMinutes: 30 },
  'Europe/Gibraltar': { latitude: 36.14, longitude: -5.35, spreadMinutes: 5 },
  'Europe/Paris': { latitude: 48.86, longitude: 2.35, spreadMinutes: 55 },
  'Europe/Monaco': { latitude: 43.73, longitude: 7.42, spreadMinutes: 5 },
  'Europe/Brussels': { latitude: 50.85, longitude: 4.35, spreadMinutes: 10 },
  'Europe/Luxembourg': { latitude: 49.61, longitude: 6.13, spreadMinutes: 5 },
  'Europe/Amsterdam': { latitude: 52.37, longitude: 4.9, spreadMinutes: 15 },
  'Europe/Berlin': { latitude: 52.52, longitude: 13.41, spreadMinutes: 45 },
  'Europe/Zurich': { latitude: 47.38, longitude: 8.54, spreadMinutes: 15 },
  'Europe/Vienna': { latitude: 48.21, longitude: 16.37, spreadMinutes: 30 },
  'Europe/Rome': { latitude: 41.9, longitude: 12.5, spreadMinutes: 30 },
  'Europe/Malta': { latitude: 35.9, longitude: 14.51, spreadMinutes: 5 },
  'Europe/Prague': { latitude: 50.08, longitude: 14.44, spreadMinutes: 35 },
  'Europe/Warsaw': { latitude: 52.23, longitude: 21.01, spreadMinutes: 35 },
  'Europe/Budapest': { latitude: 47.5, longitude: 19.04, spreadMinutes: 15 },
  'Europe/Belgrade': { latitude: 44.79, longitude: 20.45, spreadMinutes: 30 },
  'Europe/Athens': { latitude: 37.98, longitude: 23.73, spreadMinutes: 25 },
  'Europe/Bucharest': { latitude: 44.43, longitude: 26.1, spreadMinutes: 30 },
  'Europe/Sofia': { latitude: 42.7, longitude: 23.32, spreadMinutes: 25 },
  'Europe/Chisinau': { latitude: 47.01, longitude: 28.86, spreadMinutes: 10 },
  'Europe/Stockholm': { latitude: 59.33, longitude: 18.07, spreadMinutes: 195 },
  'Europe/Copenhagen': { latitude: 55.68, longitude: 12.57, spreadMinutes: 25 },
  'Europe/Oslo': { latitude: 59.91, longitude: 10.75, spreadMinutes: 280 },
  'Europe/Riga': { latitude: 56.95, longitude: 24.11, spreadMinutes: 20 },
  'Europe/Vilnius': { latitude: 54.69, longitude: 25.28, spreadMinutes: 25 },
  'Europe/Tallinn': { latitude: 59.44, longitude: 24.75, spreadMinutes: 20 },
  'Europe/Minsk': { latitude: 53.9, longitude: 27.57, spreadMinutes: 30 },
  'Europe/Kyiv': { latitude: 50.45, longitude: 30.52, spreadMinutes: 45 },
  'Europe/Istanbul': { latitude: 41.01, longitude: 28.98, spreadMinutes: 70 },
  'Europe/Helsinki': { latitude: 60.17, longitude: 24.94, spreadMinutes: 200 },
  'Europe/Moscow': { latitude: 55.76, longitude: 37.62, spreadMinutes: 315 },
  'Arctic/Longyearbyen': {
    latitude: 78.22,
    longitude: 15.63,
    spreadMinutes: 5,
  },
  'Atlantic/Jan_Mayen': {
    latitude: 70.98,
    longitude: -8.72,
    spreadMinutes: 5,
  },

  // Africa and the Middle East
  'Africa/Johannesburg': {
    latitude: -26.2,
    longitude: 28.05,
    spreadMinutes: 60,
  },
  'Africa/Cairo': { latitude: 30.04, longitude: 31.24, spreadMinutes: 25 },
  'Africa/Casablanca': { latitude: 33.57, longitude: -7.59, spreadMinutes: 30 },
  'Africa/Tunis': { latitude: 36.81, longitude: 10.18, spreadMinutes: 15 },
  'Africa/Lagos': { latitude: 6.52, longitude: 3.38, spreadMinutes: 85 },
  'Africa/Nairobi': { latitude: -1.29, longitude: 36.82, spreadMinutes: 75 },
  'Asia/Dubai': { latitude: 25.2, longitude: 55.27, spreadMinutes: 25 },
  'Asia/Yerevan': { latitude: 40.18, longitude: 44.51, spreadMinutes: 15 },
  'Asia/Tbilisi': { latitude: 41.72, longitude: 44.79, spreadMinutes: 15 },
  'Asia/Baku': { latitude: 40.41, longitude: 49.87, spreadMinutes: 25 },

  // Asia and the Pacific
  'Asia/Karachi': { latitude: 24.86, longitude: 67.01, spreadMinutes: 60 },
  'Asia/Kolkata': { latitude: 28.61, longitude: 77.21, spreadMinutes: 75 },
  'Asia/Almaty': { latitude: 43.24, longitude: 76.89, spreadMinutes: 60 },
  'Asia/Shanghai': { latitude: 31.23, longitude: 121.47, spreadMinutes: 170 },
  'Asia/Tashkent': { latitude: 41.3, longitude: 69.24, spreadMinutes: 45 },
  'Asia/Bangkok': { latitude: 13.76, longitude: 100.5, spreadMinutes: 25 },
  'Asia/Jakarta': { latitude: -6.21, longitude: 106.85, spreadMinutes: 70 },
  'Asia/Singapore': { latitude: 1.35, longitude: 103.82, spreadMinutes: 15 },
  'Asia/Hong_Kong': { latitude: 22.32, longitude: 114.17, spreadMinutes: 5 },
  'Asia/Manila': { latitude: 14.6, longitude: 120.98, spreadMinutes: 35 },
  'Asia/Seoul': { latitude: 37.57, longitude: 126.98, spreadMinutes: 20 },
  'Asia/Tokyo': { latitude: 35.68, longitude: 139.65, spreadMinutes: 85 },
  'Australia/Perth': { latitude: -31.95, longitude: 115.86, spreadMinutes: 90 },
  'Australia/Adelaide': {
    latitude: -34.93,
    longitude: 138.6,
    spreadMinutes: 20,
  },
  'Australia/Brisbane': {
    latitude: -27.47,
    longitude: 153.03,
    spreadMinutes: 55,
  },
  'Australia/Melbourne': {
    latitude: -37.81,
    longitude: 144.96,
    spreadMinutes: 10,
  },
  'Australia/Sydney': {
    latitude: -33.87,
    longitude: 151.21,
    spreadMinutes: 25,
  },
  'Australia/Hobart': {
    latitude: -42.88,
    longitude: 147.32,
    spreadMinutes: 25,
  },
  'Pacific/Auckland': {
    latitude: -36.85,
    longitude: 174.76,
    spreadMinutes: 60,
  },
}

/*
 * The ceiling is refused at load, where the table is read, so a row that
 * violates it cannot reach a send path at all. It is not clamped: clamping a
 * spread down would state a certainty the measurement denies, and quietly
 * release somebody's mail into their Shabbos.
 */
for (const [zone, place] of Object.entries(ZONE_PLACES)) {
  if (place.spreadMinutes > PLACE_SPREAD_CEILING_MINUTES) {
    throw new Error(
      `${zone} states a spread of ${place.spreadMinutes} minutes, beyond the ${PLACE_SPREAD_CEILING_MINUTES} the rest guard's arithmetic allows.`,
    )
  }
}

/**
 * The other names a zone goes by, mapped onto the one this table holds.
 *
 * A browser reports whatever its own ICU calls the system zone, and ICU builds
 * disagree about which spelling is canonical: several ship `Asia/Calcutta`,
 * `Europe/Kiev` and `America/Buenos_Aires` as the canonical form and do not
 * recognize the modern name at all. An exact key lookup therefore answers
 * `null` for people who are squarely inside a zone this table holds — and null
 * means their mail is sent whatever day it is.
 *
 * So aliases are resolved in one place rather than being patched a zone at a
 * time, and the list is swept from tzdb's own Links rather than grown a report
 * at a time — `Indian/Antananarivo` reached a million and a half people through
 * exactly the gap `Europe/Kiev` and `Asia/Calcutta` had already demonstrated. It
 * covers those Links plus the country and region shorthands (`Israel`,
 * `US/Eastern`) that older systems still report; anything else falls through as
 * itself and, if unheld, as null.
 *
 * **A Link is a claim about clocks, so two kinds of them are left out** — and
 * both are left out because an alias hands somebody the target's COORDINATES,
 * which a Link says nothing about.
 *
 *  - **A Link across a continent.** `America/Coral_Harbour` and
 *    `America/Atikokan` link onto `America/Panama` because none of them has
 *    moved a clock since 1970; Coral Harbor is at 64°N. `America/Creston` onto
 *    `America/Phoenix`, `America/Nassau` onto `America/Toronto`,
 *    `Indian/Reunion` onto `Asia/Dubai` and `Antarctica/McMurdo` onto
 *    `Pacific/Auckland` are the same shape. Each stays null, which stops a send
 *    at the caller rather than timing one against the wrong hemisphere.
 *  - **A name that is a rule rather than a place.** `EST5EDT`, `CST6CDT`,
 *    `MST7MDT`, `PST8PDT`, `EST`, `MST`, `HST`, `CET`, `EET`, `MET` and `WET`
 *    say which offset and which saving rule, and nothing whatever about where.
 *    A machine reporting `CET` is somewhere between Spain and Poland, and
 *    answering "Brussels" would be inventing the one thing this table refuses to
 *    invent.
 */
const ZONE_ALIASES: Record<string, string> = {
  Israel: 'Asia/Jerusalem',
  'Asia/Tel_Aviv': 'Asia/Jerusalem',

  'US/Eastern': 'America/New_York',
  'US/Michigan': 'America/Detroit',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Arizona': 'America/Phoenix',
  'US/Alaska': 'America/Anchorage',
  'US/Hawaii': 'Pacific/Honolulu',
  'America/Shiprock': 'America/Denver',
  Navajo: 'America/Denver',
  'America/Yellowknife': 'America/Edmonton',
  'America/Rainy_River': 'America/Winnipeg',
  'US/East-Indiana': 'America/Indiana/Indianapolis',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Fort_Wayne': 'America/Indiana/Indianapolis',
  'America/Louisville': 'America/Kentucky/Louisville',
  'Canada/Eastern': 'America/Toronto',
  'America/Montreal': 'America/Toronto',
  'America/Nipigon': 'America/Toronto',
  'America/Thunder_Bay': 'America/Toronto',
  'Canada/Atlantic': 'America/Halifax',
  'Canada/Central': 'America/Winnipeg',
  'Canada/Mountain': 'America/Edmonton',
  'Canada/Pacific': 'America/Vancouver',
  'Mexico/General': 'America/Mexico_City',
  'Mexico/BajaNorte': 'America/Tijuana',
  'America/Ensenada': 'America/Tijuana',
  'America/Santa_Isabel': 'America/Tijuana',

  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'Brazil/East': 'America/Sao_Paulo',
  'Chile/Continental': 'America/Santiago',

  GB: 'Europe/London',
  'GB-Eire': 'Europe/London',
  'Europe/Belfast': 'Europe/London',
  Eire: 'Europe/Dublin',
  Portugal: 'Europe/Lisbon',
  'Europe/Vatican': 'Europe/Rome',
  'Europe/San_Marino': 'Europe/Rome',
  'Europe/Busingen': 'Europe/Zurich',
  'Europe/Vaduz': 'Europe/Zurich',
  'Europe/Bratislava': 'Europe/Prague',
  'Europe/Guernsey': 'Europe/London',
  'Europe/Jersey': 'Europe/London',
  'Europe/Isle_of_Man': 'Europe/London',
  'Europe/Mariehamn': 'Europe/Helsinki',
  'W-SU': 'Europe/Moscow',
  Poland: 'Europe/Warsaw',
  'Europe/Zagreb': 'Europe/Belgrade',
  'Europe/Ljubljana': 'Europe/Belgrade',
  'Europe/Sarajevo': 'Europe/Belgrade',
  'Europe/Skopje': 'Europe/Belgrade',
  'Europe/Podgorica': 'Europe/Belgrade',
  'Europe/Kiev': 'Europe/Kyiv',
  'Europe/Uzhgorod': 'Europe/Kyiv',
  'Europe/Zaporozhye': 'Europe/Kyiv',
  'Europe/Tiraspol': 'Europe/Chisinau',
  'Asia/Istanbul': 'Europe/Istanbul',
  Turkey: 'Europe/Istanbul',

  Egypt: 'Africa/Cairo',
  'Africa/Maseru': 'Africa/Johannesburg',
  'Africa/Mbabane': 'Africa/Johannesburg',
  'Africa/Bangui': 'Africa/Lagos',
  'Africa/Brazzaville': 'Africa/Lagos',
  'Africa/Douala': 'Africa/Lagos',
  'Africa/Kinshasa': 'Africa/Lagos',
  'Africa/Libreville': 'Africa/Lagos',
  'Africa/Luanda': 'Africa/Lagos',
  'Africa/Malabo': 'Africa/Lagos',
  'Africa/Niamey': 'Africa/Lagos',
  'Africa/Porto-Novo': 'Africa/Lagos',
  'Africa/Addis_Ababa': 'Africa/Nairobi',
  'Africa/Asmara': 'Africa/Nairobi',
  'Africa/Asmera': 'Africa/Nairobi',
  'Africa/Dar_es_Salaam': 'Africa/Nairobi',
  'Africa/Djibouti': 'Africa/Nairobi',
  'Africa/Kampala': 'Africa/Nairobi',
  'Africa/Mogadishu': 'Africa/Nairobi',
  'Indian/Antananarivo': 'Africa/Nairobi',
  'Indian/Comoro': 'Africa/Nairobi',
  'Indian/Mayotte': 'Africa/Nairobi',

  Hongkong: 'Asia/Hong_Kong',
  'Asia/Chongqing': 'Asia/Shanghai',
  'Asia/Chungking': 'Asia/Shanghai',
  'Asia/Harbin': 'Asia/Shanghai',
  PRC: 'Asia/Shanghai',
  'Asia/Muscat': 'Asia/Dubai',
  'Asia/Phnom_Penh': 'Asia/Bangkok',
  'Asia/Vientiane': 'Asia/Bangkok',
  Singapore: 'Asia/Singapore',
  'Asia/Kuala_Lumpur': 'Asia/Singapore',
  ROK: 'Asia/Seoul',
  Japan: 'Asia/Tokyo',
  'Australia/ACT': 'Australia/Sydney',
  'Australia/Canberra': 'Australia/Sydney',
  'Australia/NSW': 'Australia/Sydney',
  'Australia/Victoria': 'Australia/Melbourne',
  'Australia/Queensland': 'Australia/Brisbane',
  'Australia/South': 'Australia/Adelaide',
  'Australia/West': 'Australia/Perth',
  'Australia/Tasmania': 'Australia/Hobart',
  'Australia/Currie': 'Australia/Hobart',
  NZ: 'Pacific/Auckland',
}

/**
 * The one name this platform holds a zone under.
 *
 * Applied when a zone is WRITTEN, so the stored value is one spelling, and
 * again when it is read, since rows already stored were written by whatever
 * the browser said and a legacy name may arrive from anywhere.
 */
export const canonicalZone = (timeZoneId: string): string =>
  ZONE_ALIASES[timeZoneId] ?? timeZoneId

/** The one zone that decides a one-day Yom Tov rather than a two-day one. */
export const isIsraelZone = (timeZoneId: string): boolean =>
  canonicalZone(timeZoneId) === 'Asia/Jerusalem'

/**
 * Roughly where this zone is, or null where we do not know.
 *
 * Null is an answer, not a failure to produce one: it says this person's
 * whereabouts are unknown, which is a different thing from their being
 * somewhere average.
 */
export function placeForZone(timeZoneId: string): ZonePlace | null {
  const held = ZONE_PLACES[canonicalZone(timeZoneId)]
  if (!held) return null
  /* The floor is applied on the way out rather than trusted in the row: a
     spread is a measurement, and a measurement of nearly zero still describes a
     city rather than a person standing in the middle of it. */
  return held.spreadMinutes >= PLACE_SPREAD_FLOOR_MINUTES
    ? held
    : { ...held, spreadMinutes: PLACE_SPREAD_FLOOR_MINUTES }
}

/** Every zone this table holds, for the checks that must cover all of them. */
export const heldZones = (): string[] => Object.keys(ZONE_PLACES)

/**
 * How far a representative may be and still be somebody's clock, in kilometers.
 *
 * A borrowed zone must never be a borrowed DAY. Geometry cannot rule that out
 * on its own: the date line is drawn by politics, so coordinates in the central
 * Pacific sit beside a representative whose civil date is a day behind them —
 * Kiritimati keeps UTC+14 and its nearest neighbor in this table is Honolulu,
 * two thousand kilometers away and twenty-four hours out. What separates that
 * case from a real neighbor is DISTANCE: every place where the date line
 * leaves the meridian is a remote island, and no representative is within reach
 * of one. So the borrow is bounded, and past the bound the answer is null.
 *
 * Inside it the clocks differ by at most a couple of hours, which is the whole
 * cost being accepted here — the coordinates still give the sunset, and the
 * zone decides only where the civil day is cut.
 */
const ZONE_BORROW_LIMIT_KM = 1500

/** Degrees of latitude to kilometers, near enough for a radius. */
const KM_PER_DEGREE = 111.32

/**
 * Which zone's clock to read at these coordinates, for somebody whose place is
 * known exactly and whose zone is not.
 *
 * The nearest representative within `ZONE_BORROW_LIMIT_KM`, and null beyond it.
 * Borrowing a nearby clock is a weaker claim than it looks and worth making:
 * against it is dropping an exact address altogether and sending into somebody's
 * Shabbos, which is what answering null costs.
 *
 * Distance is flat-earth on purpose: at the scale of "which of these cities is
 * closest" the curvature changes no answer, and longitude is weighted by
 * latitude so the comparison stays a distance rather than a difference of
 * coordinates.
 */
export function zoneNearest(
  latitude: number,
  longitude: number,
): string | null {
  let best: string | null = null
  let bestD = Number.POSITIVE_INFINITY
  const scale = Math.cos((latitude * Math.PI) / 180)
  for (const [zone, place] of Object.entries(ZONE_PLACES)) {
    const dy = place.latitude - latitude
    /* Wrapped, so a place just east of the antimeridian is near Auckland
       rather than most of a planet away from it. */
    let dl = place.longitude - longitude
    if (dl > 180) dl -= 360
    if (dl < -180) dl += 360
    const dx = dl * scale
    const d = dy * dy + dx * dx
    if (d < bestD) {
      bestD = d
      best = zone
    }
  }
  return Math.sqrt(bestD) * KM_PER_DEGREE <= ZONE_BORROW_LIMIT_KM ? best : null
}
